/**
 * Character Styles Rules Engine
 *
 * Evaluates profile-driven character style rules and emits declarative
 * actions for the Word applicator. The engine is pure — it does not call
 * Word APIs — so it can be reused for review re-apply later.
 *
 * Implemented rules:
 *   Italic group:      I-1, I-2, I-3, I-4, I-5, I-6, I-7
 *   Bold group:        B-1, B-2, B-3
 *   Bold Italic group: BI-1, BI-4
 *   URL group:         URL-1
 *   Fraction group:    FRAC-1
 *   Small Caps group:  SC-1, SC-2, SC-3, SC-4
 *
 * Rule execution order per paragraph:
 *   1. Italic rules (I-1 → I-7)  — removal, roman spans, entity exceptions, full-paragraph conversion
 *   2. Bold rules   (B-1 → B-3)  — span-level removal, bold-italic conversion, default bold conversion
 *
 * All category lists and style mappings are read from the profile's
 * character_styles array so future profiles work without code changes.
 *
 * @see docs/requirements/character-styles/rules.md — canonical rule definitions
 * @see docs/requirements/character-styles/design.md — architecture rationale
 */

import type { CharacterStyleProfileEntry, EntityHint } from "src/types";
import type {
  CharacterStyleAction,
  CharStyleFormattingOrigin,
  FormattingSpan,
  FormattingType,
  ParagraphFormattingInfo,
  RuleContext,
} from "./types";

// ── Reusable helpers (shared across italic / bold / future rule groups) ──

/**
 * Find the profile character_styles entry that maps a given Word formatting
 * type (e.g. "italic", "bold") to a profile style code (e.g. "i", "b").
 *
 * @param characterStyles - The character_styles array from the loaded profile.
 * @param sourceStyle - The Word formatting name to look up (matches source_style field).
 * @returns The matching profile entry, or undefined if the profile has no mapping.
 */
function findConfigBySourceStyle(
  characterStyles: CharacterStyleProfileEntry[],
  sourceStyle: string,
): CharacterStyleProfileEntry | undefined {
  return characterStyles.find((entry) => entry.source_style === sourceStyle);
}

/**
 * Check whether a formatting type should be removed for a given paragraph.
 *
 * Removal triggers if the paragraph's category is in removal_categories
 * OR the paragraph's style code is in removal_styles.
 *
 * @param config - The profile entry for the formatting type.
 * @param paragraphCategory - The paragraph's category (e.g. "Extract", "Epigraph").
 * @param paragraphStyle - The paragraph's style code (e.g. "cn", "ct", "ext").
 * @returns True if removal applies for this paragraph.
 */
function shouldRemoveFormatting(
  config: CharacterStyleProfileEntry,
  paragraphCategory: string,
  paragraphStyle: string,
): boolean {
  return (
    config.removal_categories.includes(paragraphCategory) ||
    config.removal_styles.includes(paragraphStyle)
  );
}

/**
 * Check whether removal is triggered specifically by the paragraph's style code.
 * When true, entity hint exceptions should NOT be applied — the formatting is
 * removed unconditionally.
 *
 * @param config - The profile entry for the formatting type.
 * @param paragraphStyle - The paragraph's style code (e.g. "cn", "ct").
 * @returns True if removal is triggered by removal_styles.
 */
function isRemovalByStyle(
  config: CharacterStyleProfileEntry,
  paragraphStyle: string,
): boolean {
  return config.removal_styles.includes(paragraphStyle);
}

/**
 * Check whether a character style can be applied in a given paragraph category.
 * An empty applies_to list means the style applies everywhere.
 *
 * @param config - The profile entry for the character style.
 * @param paragraphCategory - The paragraph's category.
 * @returns True if the style is allowed in this category.
 */
function canApplyStyle(
  config: CharacterStyleProfileEntry,
  paragraphCategory: string,
): boolean {
  if (config.applies_to.length === 0) {
    return true;
  }
  return config.applies_to.includes(paragraphCategory);
}

/**
 * Build a lookup map from Word formatting name → profile style code.
 * e.g. { italic: "i", bold: "b", underline: "u", strikethrough: "strk" }
 *
 * @param characterStyles - The character_styles array from the loaded profile.
 * @returns Map where keys are source_style values and values are style codes.
 */
function buildSourceStyleMap(
  characterStyles: CharacterStyleProfileEntry[],
): Map<string, string> {
  const map = new Map<string, string>();
  for (const entry of characterStyles) {
    if (entry.source_style) {
      map.set(entry.source_style, entry.style);
    }
  }
  return map;
}

/**
 * Filter entity hints to types that are relevant for italic emphasis exceptions.
 * Used by I-4, I-5, and I-7 to identify publication names and foreign words.
 *
 * @param hints - All entity hints for the paragraph.
 * @returns Only hints with type "publication" or "foreign_word".
 */
function getItalicEntityHints(hints: EntityHint[]): EntityHint[] {
  return hints.filter(
    (hint) => hint.type === "publication" || hint.type === "foreign_word",
  );
}

/**
 * Check if a formatting span overlaps with any entity hint by index position.
 *
 * Uses precise range overlap detection instead of substring matching to avoid
 * false positives (e.g., short word "I" being treated as substring of "Maria:").
 *
 * @param span - The formatting span to check.
 * @param entityHints - Entity hints filtered to relevant types.
 * @returns True if the span's index range overlaps with any entity hint range.
 */
function isSpanCoveredByEntityHint(
  span: FormattingSpan,
  entityHints: EntityHint[],
): boolean {
  for (const hint of entityHints) {
    const entityStart = hint.start_index;
    const entityEnd = hint.start_index + hint.text.length;
    
    // Check if span and entity ranges overlap
    // Overlap exists if: span.start < entityEnd AND entityStart < span.end
    if (span.start < entityEnd && entityStart < span.end) {
      return true;
    }
  }
  return false;
}

// ── Italic rules (I-1 through I-6, I-8) ───────────────────────────

/**
 * Evaluate italic rules for a single paragraph.
 *
 * Rule priority:
 *   I-1 fires when majority/all italic AND paragraph is in a removal category.
 *     Sub-steps: I-2 (roman spans → "r"), I-3 (preserve other formatting),
 *                I-4/I-5 (entity hint exceptions).
 *   I-6 fires when ALL italic AND paragraph is NOT in a removal category.
 *     Applies "i" to paragraph and preserves other formatting.
 *   I-8 fires when partial italic AND paragraph is NOT in a removal category.
 *     Applies "i" to italic spans (default conversion).
 *   I-6 and I-8 leave underline+italic spans to U-4 ("u-i") when U-4 applies.
 *
 * NOTE: I-7 (entity hints → "r" for visual distinction) was removed as it
 * incorrectly applied to quote sources where publications should remain italic.
 *
 * @param context - Complete rule evaluation context for the paragraph.
 * @returns Array of declarative actions for the applicator.
 */
export function evaluateItalicRules(context: RuleContext): CharacterStyleAction[] {
  const italicConfig = findConfigBySourceStyle(context.characterStyles, "italic");
  if (!italicConfig) {
    return [];
  }

  const { formatting, paragraphCategory, paragraphStyle, entityHints, characterStyles } = context;
  const sourceStyleMap = buildSourceStyleMap(characterStyles);
  const relevantEntityHints = getItalicEntityHints(entityHints);
  const isRemoval = shouldRemoveFormatting(italicConfig, paragraphCategory, paragraphStyle);
  const styleTriggered = isRemovalByStyle(italicConfig, paragraphStyle);
  // When U-1 removes underline here, I-3/I-6 must not preserve it as "u".
  const uConfig = findConfigBySourceStyle(characterStyles, "underline");
  const underlineRemoved =
    !!uConfig && shouldRemoveFormatting(uConfig, paragraphCategory, paragraphStyle);

  // I-1: Remove italic formatting (majority/all italic + removal category/style)
  if (formatting.majorityItalic && isRemoval) {
    // When removal is triggered by style code, skip entity hint exceptions
    const hints = styleTriggered ? [] : relevantEntityHints;
    return evaluateItalicRemoval(
      formatting,
      italicConfig,
      paragraphCategory,
      hints,
      sourceStyleMap,
      underlineRemoved,
    );
  }

  const ownedByU4 = underlineItalicOwnership(context);

  // I-6: Convert full-paragraph italics to "i" (all italic + NOT removal category)
  if (formatting.allItalic && canApplyStyle(italicConfig, paragraphCategory)) {
    return evaluateFullItalicConversion(
      formatting,
      italicConfig,
      relevantEntityHints,
      sourceStyleMap,
      ownedByU4,
      underlineRemoved,
    );
  }

  // I-8 (implicit default): Convert remaining italic spans to "i" in non-removal categories
  if (!isRemoval && canApplyStyle(italicConfig, paragraphCategory)) {
    // console.log(`[Italic Rules] → I-8 PARTIAL CONVERSION`);
    return evaluatePartialItalicConversion(formatting, italicConfig, ownedByU4);
  }

  return [];
}

/**
 * Build a predicate for italic spans that U-4 will style as "u-i". Italic rules
 * must not emit for these spans: filterOverlappingCcActions keeps only the first
 * action per span, and italic actions precede underline actions.
 */
function underlineItalicOwnership(context: RuleContext): (span: FormattingSpan) => boolean {
  const { characterStyles, paragraphCategory, paragraphStyle } = context;
  const uConfig = findConfigBySourceStyle(characterStyles, "underline");
  const uiConfig = findConfigBySourceStyle(characterStyles, "underline,italic");
  if (
    !uConfig ||
    !uiConfig ||
    shouldRemoveFormatting(uConfig, paragraphCategory, paragraphStyle) ||
    !canApplyStyle(uiConfig, paragraphCategory)
  ) {
    return () => false;
  }
  return (span) =>
    span.italic && span.underline && !HYPERLINK_CHARACTER_STYLES.has(span.characterStyle ?? "");
}

/**
 * Convert italic spans to "i" when the paragraph is not in a removal category
 * and the italics do not cover the full paragraph. This handles inline emphasis
 * inside body text without removing formatting. Underline+italic spans are
 * skipped so U-4 can apply "u-i".
 */
function evaluatePartialItalicConversion(
  formatting: ParagraphFormattingInfo,
  italicConfig: CharacterStyleProfileEntry,
  ownedByU4: (span: FormattingSpan) => boolean,
): CharacterStyleAction[] {
  const actions: CharacterStyleAction[] = [];

  for (let spanIndex = 0; spanIndex < formatting.spans.length; spanIndex++) {
    const span = formatting.spans[spanIndex];
    if (!span.text.trim() || !span.italic) {
      continue;
    }
    // Skip bold+italic spans — handled by BI rules
    if (span.bold) {
      continue;
    }
    if (ownedByU4(span)) {
      continue;
    }
    actions.push({
      type: "apply_style",
      style: italicConfig.style,
      target: "span",
      spanIndex,
      tracked: true,
      formattingOrigin: "source",
    });
  }

  return actions;
}

/**
 * I-1 flow: Remove italic formatting from a majority-italic paragraph
 * in a removal category, preserving roman spans, entity exceptions,
 * and other inline formatting.
 *
 * Sub-rules applied:
 *   I-2: Apply "r" to non-italic (roman) spans
 *   I-3: Preserve other formatting (bold → "b", underline → "u", etc.).
 *        Underline is not preserved when U-1 removes it too (U-1 then records
 *        the span as an underline+italic removal).
 *   I-4: Maintain italics on publication name entity hints
 *   I-5: Maintain italics on foreign/translated word entity hints
 */
function evaluateItalicRemoval(
  formatting: ParagraphFormattingInfo,
  italicConfig: CharacterStyleProfileEntry,
  paragraphCategory: string,
  entityHints: EntityHint[],
  sourceStyleMap: Map<string, string>,
  underlineRemoved: boolean,
): CharacterStyleAction[] {
  const actions: CharacterStyleAction[] = [];

  // Step 1: Before removing italic, identify spans to preserve

  for (let spanIndex = 0; spanIndex < formatting.spans.length; spanIndex++) {
    const span = formatting.spans[spanIndex];
    if (!span.text.trim()) {
      continue; // skip whitespace-only spans
    }

    if (!span.italic) {
      // I-2: Non-italic (roman) span in a majority-italic paragraph → apply "r".
      // Not for the roman part of a partly-italic word (e.g. "—the"): it is
      // already roman, and only the italic part of that word needs a style.
      if (span.characterStyle?.toLowerCase() !== "r" && !span.partialWordRemainder) {
        // console.log(
        //   `[I-2] Applying "r" to span[${spanIndex}] "${span.text}" (non-italic in removal paragraph)`,
        // );
        actions.push({
          type: "apply_style",
          style: "r",
          target: "span",
          spanIndex,
          tracked: true,
          formattingOrigin: "inference",
        });
      }
      continue;
    }

    // Span is italic — check if it's an entity exception (I-4 / I-5)
    if (isSpanCoveredByEntityHint(span, entityHints)) {
      // I-4/I-5: This span is a publication or foreign word — will re-apply "i" after removal
      actions.push({
        type: "apply_style",
        style: italicConfig.style,
        target: "span",
        spanIndex,
        tracked: true,
        formattingOrigin: "inference",
      });
    }

    // I-3: Preserve other formatting that coexists with italic
    emitPreservedFormattingActions(span, spanIndex, sourceStyleMap, actions, "inference", underlineRemoved);
  }

  // Step 2: Also emit entity-level apply_style for hints (search-based fallback)
  // This covers cases where entity text spans multiple word ranges
  for (const hint of entityHints) {
    actions.push({
      type: "apply_style",
      style: italicConfig.style,
      target: "entity",
      targetText: hint.text,
      targetStartIndex: hint.start_index,
      ruleId: hint.type === "publication" ? "I-4" : "I-5",
      entityType: hint.type,
      tracked: true,
      formattingOrigin: "inference",
    });
  }

  // Step 3: Remove italic from the paragraph (tracked change)
  actions.push({ type: "remove_formatting", formatting: "italic", target: "paragraph", tracked: true, formattingOrigin: "inference" });

  return actions;
}

/**
 * I-6 flow: Convert full-paragraph italic to "i" character style.
 *
 * Applies "i" to the entire paragraph if it does not contain entity hints, then preserves other inline formatting
 * (bold, underline, etc.) on individual spans.
 *
 * Underline+italic spans are left to U-4 ("u-i"): no "u" is preserved for them,
 * and the paragraph "i" is skipped when every text span is underline+italic.
 * Underline is also not preserved when U-1 removes it (e.g. Table of Contents).
 *
 * NOTE: Previous implementation included I-7 (entity hints → "r" for visual
 * distinction), but this was incorrect for quote sources (fsqs, fsqsc) where
 * the publication name should remain italic as part of the attribution.
 */
function evaluateFullItalicConversion(
  formatting: ParagraphFormattingInfo,
  italicConfig: CharacterStyleProfileEntry,
  entityHints: EntityHint[],
  sourceStyleMap: Map<string, string>,
  ownedByU4: (span: FormattingSpan) => boolean,
  underlineRemoved: boolean,
): CharacterStyleAction[] {
  const actions: CharacterStyleAction[] = [];
  const textSpans = formatting.spans.filter((span) => span.text.trim());

  // I-6: Apply "i" to the full paragraph, unless U-4 styles all of it as "u-i"
  if (
    !textSpans.every(ownedByU4) &&
    !formatting.spans.some((span) => isSpanCoveredByEntityHint(span, entityHints))
  ) {
    let textToApply = formatting.spans.map((s) => s.text).join("");
    actions.push({
      type: "apply_style",
      style: italicConfig.style,
      target: "paragraph",
      targetStartIndex: formatting.spans[0].start,
      targetText: textToApply,
      tracked: true,
      formattingOrigin: "source",
    });
  }

  // REMOVED: I-7 (entity hints → "r") - incorrect for quote sources
  // Entity hints in fully-italic paragraphs should remain italic

  // Preserve other inline formatting (bold spans → "b", etc.)
  for (let spanIndex = 0; spanIndex < formatting.spans.length; spanIndex++) {
    const span = formatting.spans[spanIndex];
    if (!span.text.trim()) {
      continue;
    }
    emitPreservedFormattingActions(
      span,
      spanIndex,
      sourceStyleMap,
      actions,
      "source",
      ownedByU4(span) || underlineRemoved,
    );
  }

  return actions;
}

// ── Bold rules (B-1 through B-3) ──────────────────────────────────

/**
 * Evaluate bold rules B-1 through B-3 for a single paragraph.
 *
 * Unlike italic rules, B-1 operates at the span level: individual bold
 * spans are removed if the paragraph is in a formatting-removal category,
 * regardless of whether the whole paragraph is bold.
 *
 * Rule priority:
 *   B-1: Remove bold from individual spans when paragraph is in a removal category.
 *     Sub-step: B-2 (bold-italic spans → "i" after bold removal).
 *   B-3: Convert remaining bold spans to "b" (paragraph NOT in removal category).
 *
 * @param context - Complete rule evaluation context for the paragraph.
 * @returns Array of declarative actions for the applicator.
 */
export function evaluateBoldRules(context: RuleContext): CharacterStyleAction[] {
  const boldConfig = findConfigBySourceStyle(context.characterStyles, "bold");
  if (!boldConfig) {
    return [];
  }

  const { formatting, paragraphCategory, paragraphStyle, entityHints } = context;
  const actions: CharacterStyleAction[] = [];
  const isRemoval = shouldRemoveFormatting(boldConfig, paragraphCategory, paragraphStyle);
  const styleTriggered = isRemovalByStyle(boldConfig, paragraphStyle);
  // Skip speaker hint exceptions when removal is triggered by style code
  const speakerHints = styleTriggered ? [] : entityHints.filter((hint) => hint.type === "speaker");

  for (let spanIndex = 0; spanIndex < formatting.spans.length; spanIndex++) {
    const span = formatting.spans[spanIndex];
    if (!span.text.trim() || !span.bold) {
      continue; // skip non-bold or whitespace-only spans
    }
    // Skip bold+italic spans — handled by BI rules
    if (span.italic) {
      continue;
    }
    
    //**This below is not required as it is covered in the respective rules
    // Skip spans covered by a speaker entity hint — speaker/TM rules
    // apply a more specific character style (diaspk, tmspkc).
    /*if (speakerHints.length > 0 && isSpanCoveredByEntityHint(span, speakerHints)) {
      console.log(`[Bold Rules] → span[${spanIndex}] "${span.text}" (bold=${span.bold}, italic=${span.italic}), SKIPPING due to speaker entity hint`);
      continue;
    }*/

    //console.log(`[Bold Rules] → span[${spanIndex}] "${span.text}" (bold=${span.bold}, italic=${span.italic}), isRemoval=${isRemoval}, canApplyStyle=${canApplyStyle(boldConfig, paragraphCategory)}`);
    
    if (isRemoval) {
      // B-1: Remove bold from this span (tracked change)
      actions.push({
        type: "remove_formatting",
        formatting: "bold",
        target: "span",
        spanIndex,
        tracked: true,
        formattingOrigin: "inference",
      });
    } else if (canApplyStyle(boldConfig, paragraphCategory)) {
      // B-3: Convert bold span to "b" character style
      //console.log(`[B-3] Applying "b" to span[${spanIndex}] "${span.text}" (bold in non-removal paragraph)`);
      actions.push({
        type: "apply_style",
        style: boldConfig.style,
        target: "span",
        spanIndex,
        tracked: true,
        formattingOrigin: "source",
      });
    }
  }

  return actions;
}

// ── Bold Italic rules (BI-1 through BI-4) ────────────────────────

/**
 * Evaluate bold-italic rules BI-1 through BI-4 for a single paragraph.
 *
 * Follows the same pattern as bold rules but targets spans where both
 * bold AND italic are true. Uses the profile entry with
 * source_style "bold,italic" → style "bi".
 *
 * Rule priority:
 *   BI-1: Remove bold+italic from spans when paragraph is in a removal category.
 *   BI-2: Publication name spans that are bold+italic → "i" (regardless of category).
 *   BI-3: Foreign/translated word spans that are bold+italic → "i" (regardless of category).
 *   BI-4: Convert remaining bold+italic spans to "bi" (paragraph NOT in removal category).
 *
 * @param context - Complete rule evaluation context for the paragraph.
 * @returns Array of declarative actions for the applicator.
 */
export function evaluateBoldItalicRules(context: RuleContext): CharacterStyleAction[] {
  const biConfig = findConfigBySourceStyle(context.characterStyles, "bold,italic");
  if (!biConfig) {
    return [];
  }

  const italicConfig = findConfigBySourceStyle(context.characterStyles, "italic");
  const { formatting, paragraphCategory, paragraphStyle, entityHints } = context;
  const actions: CharacterStyleAction[] = [];
  const isRemoval = shouldRemoveFormatting(biConfig, paragraphCategory, paragraphStyle);
  const styleTriggered = isRemovalByStyle(biConfig, paragraphStyle);
  // Skip entity hints when removal is triggered by style code
  const relevantEntityHints = styleTriggered ? [] : getItalicEntityHints(entityHints);

  for (let spanIndex = 0; spanIndex < formatting.spans.length; spanIndex++) {
    const span = formatting.spans[spanIndex];
    if (!span.text.trim() || !span.bold || !span.italic) {
      continue; // only handle spans that are both bold AND italic
    }

    // BI-2/BI-3: Publication names and foreign words → "i" (only for category-based removal)
    if (
      italicConfig &&
      relevantEntityHints.length > 0 &&
      isSpanCoveredByEntityHint(span, relevantEntityHints)
    ) {
      actions.push({
        type: "apply_style",
        style: italicConfig.style,
        target: "span",
        spanIndex,
        tracked: true,
        formattingOrigin: "inference",
      });
      continue;
    }

    if (isRemoval) {
      // BI-1: Remove both bold and italic from this span
      actions.push({ type: "remove_formatting", formatting: "bold", target: "span", spanIndex, tracked: true, formattingOrigin: "inference" });
      actions.push({ type: "remove_formatting", formatting: "italic", target: "span", spanIndex, tracked: true, formattingOrigin: "inference" });
    } else if (canApplyStyle(biConfig, paragraphCategory)) {
      // BI-4: Convert bold+italic span to "bi" character style
      actions.push({
        type: "apply_style",
        style: biConfig.style,
        target: "span",
        spanIndex,
        tracked: true,
        formattingOrigin: "source",
      });
    }
  }

  // BI-2/BI-3: Also emit entity-level action for multi-word entity coverage.
  // Only emit in removal categories — these are "re-apply after removal" fallbacks.
  // In non-removal paragraphs, I-8 owns italic spans and BI-4 owns bold+italic spans.
  if (isRemoval && italicConfig && relevantEntityHints.length > 0) {
    for (const hint of relevantEntityHints) {
      actions.push({
        type: "apply_style",
        style: italicConfig.style,
        target: "entity",
        targetText: hint.text,
        targetStartIndex: hint.start_index,
        tracked: true,
        formattingOrigin: "inference",
      });
    }
  }

  return actions;
}

// ── Shared preservation logic ─────────────────────────────────────

/**
 * Emit actions to preserve non-italic formatting on a span as profile
 * character styles. Called by I-1 (I-3 sub-step) and I-6 to restore
 * formatting that coexists with italic.
 *
 * Mapping: bold → "b", underline → "u", strikethrough → "strk"
 * (via source_style lookup in the profile).
 *
 * @param span - The formatting span to inspect.
 * @param spanIndex - Index of this span in ParagraphFormattingInfo.spans.
 * @param sourceStyleMap - Map from Word format name to profile style code.
 * @param actions - The actions array to push preserve actions onto.
 * @param formattingOrigin - "inference" under I-1 (italic was removed), "source" under I-6.
 */
function emitPreservedFormattingActions(
  span: FormattingSpan,
  spanIndex: number,
  sourceStyleMap: Map<string, string>,
  actions: CharacterStyleAction[],
  formattingOrigin: CharStyleFormattingOrigin,
  skipUnderline = false,
): void {
  const preserveMap: Array<[boolean, FormattingType]> = [
    // Bold+italic spans are owned by BI rules (BI-4 / BI-1); emitting "b" here
    // would claim the spanIndex in filterOverlappingCcActions before BI can
    // emit "bi", causing the correct BI action to be silently dropped.
    // Underline is skipped when U-4 owns the span ("u-i") or U-1 removes it;
    // a preserved "u" would otherwise re-apply the underline U-1 removes.
    [span.bold && !span.italic, "bold"],
    [span.underline && !skipUnderline, "underline"],
    [span.strikethrough, "strikethrough"],
  ];

  for (const [isActive, formatType] of preserveMap) {
    if (!isActive) {
      continue;
    }
    const profileStyle = sourceStyleMap.get(formatType);
    if (profileStyle) {
      actions.push({
        type: "apply_style",
        style: profileStyle,
        target: "span",
        spanIndex,
        tracked: true,
        formattingOrigin,
      });
    }
  }
}

// ── Underline rules (U-1 through U-5) ─────────────────────────────

/**
 * Word's built-in hyperlink character styles, lowercased to match
 * `normalizeStyle`. `FollowedHyperlink` is what a clicked link carries.
 */
export const HYPERLINK_CHARACTER_STYLES = new Set(["hyperlink", "followedhyperlink"]);

/**
 * Evaluate underline rules U-1 through U-5 for a single paragraph.
 *
 * Rule priority:
 *   U-1: Remove underline when paragraph is in a removal category. Italic+underline
 *        spans also lose italic when italic is removed here, giving one
 *        "underline,italic" removal record (Underline Italic removed).
 *   U-2: Publication name spans → "i" (regardless of category).
 *   U-3: Foreign/translated word spans → "i" (regardless of category).
 *   U-4: Underline + italic spans → "u-i" (not in removal category).
 *   U-5: Underline-only spans → "u" (not in removal category).
 *
 * @param context - Complete rule evaluation context for the paragraph.
 * @returns Array of declarative actions for the applicator.
 */
export function evaluateUnderlineRules(context: RuleContext): CharacterStyleAction[] {
  const uConfig = findConfigBySourceStyle(context.characterStyles, "underline");
  if (!uConfig) {
    return [];
  }

  const uiConfig = findConfigBySourceStyle(context.characterStyles, "underline,italic");
  const italicConfig = findConfigBySourceStyle(context.characterStyles, "italic");
  const { formatting, paragraphCategory, paragraphStyle, entityHints } = context;
  const actions: CharacterStyleAction[] = [];
  const isRemoval = shouldRemoveFormatting(uConfig, paragraphCategory, paragraphStyle);
  const italicRemoved =
    !!italicConfig && shouldRemoveFormatting(italicConfig, paragraphCategory, paragraphStyle);
  const styleTriggered = isRemovalByStyle(uConfig, paragraphStyle);
  // Skip entity hints when removal is triggered by style code
  const relevantEntityHints = styleTriggered ? [] : getItalicEntityHints(entityHints);

  for (let spanIndex = 0; spanIndex < formatting.spans.length; spanIndex++) {
    const span = formatting.spans[spanIndex];
    if (!span.text.trim() || !span.underline) {
      continue; // skip non-underline or whitespace-only spans
    }

    // Please remove once hyperlinks are stripped of their character styles.
    // A hyperlink's underline is Word's own chrome, not authorial formatting, so
    // none of the underline rules should act on it. This also avoids a hard
    // failure: a generated table of contents is built from HYPERLINK/PAGEREF
    // fields, and a content control cannot be inserted into a field result — the
    // insert is rejected with GeneralException.
    if (HYPERLINK_CHARACTER_STYLES.has(span.characterStyle ?? "")) {
      continue;
    }

    // U-2/U-3: Publication names and foreign words → "i" (only for category-based removal)
    if (
      italicConfig &&
      relevantEntityHints.length > 0 &&
      isSpanCoveredByEntityHint(span, relevantEntityHints)
    ) {
      actions.push({
        type: "apply_style",
        style: italicConfig.style,
        target: "span",
        spanIndex,
        tracked: true,
        formattingOrigin: "inference",
      });
      continue;
    }

    if (isRemoval) {
      // U-1: Remove underline from this span
      actions.push({
        type: "remove_formatting",
        formatting: "underline",
        target: "span",
        spanIndex,
        tracked: true,
        formattingOrigin: "inference",
      });
      // Emitted after underline so the merged record reads "underline,italic" (like BI-1).
      if (span.italic && italicRemoved) {
        actions.push({ type: "remove_formatting", formatting: "italic", target: "span", spanIndex, tracked: true, formattingOrigin: "inference" });
      }
    } else if (span.italic && uiConfig && canApplyStyle(uiConfig, paragraphCategory)) {
      // U-4: Underline + italic → "u-i"
      actions.push({
        type: "apply_style",
        style: uiConfig.style,
        target: "span",
        spanIndex,
        tracked: true,
        formattingOrigin: "source",
      });
    } else if (canApplyStyle(uConfig, paragraphCategory)) {
      // U-5: Underline-only → "u"
      actions.push({
        type: "apply_style",
        style: uConfig.style,
        target: "span",
        spanIndex,
        tracked: true,
        formattingOrigin: "source",
      });
    }
  }

  // U-2/U-3: Entity-level action for multi-word entities
  if (italicConfig && relevantEntityHints.length > 0) {
    for (const hint of relevantEntityHints) {
      actions.push({
        type: "apply_style",
        style: italicConfig.style,
        target: "entity",
        targetText: hint.text,
        targetStartIndex: hint.start_index,
        tracked: true,
        formattingOrigin: "inference",
      });
    }
  }

  return actions;
}

// ── Strikethrough rules (S-1) ─────────────────────────────────────

/**
 * Evaluate strikethrough rule S-1 for a single paragraph.
 *
 * S-1: Convert any strikethrough span to "strk", regardless of
 * paragraph category. No removal categories or exceptions apply.
 *
 * @param context - Complete rule evaluation context for the paragraph.
 * @returns Array of declarative actions for the applicator.
 */
export function evaluateStrikethroughRules(context: RuleContext): CharacterStyleAction[] {
  const strkConfig = findConfigBySourceStyle(context.characterStyles, "strikethrough");
  if (!strkConfig) {
    return [];
  }

  const { formatting } = context;
  const actions: CharacterStyleAction[] = [];

  for (let spanIndex = 0; spanIndex < formatting.spans.length; spanIndex++) {
    const span = formatting.spans[spanIndex];
    if (!span.text.trim() || !span.strikethrough) {
      continue;
    }
    // S-1: Convert strikethrough span to "strk"
    actions.push({
      type: "apply_style",
      style: strkConfig.style,
      target: "span",
      spanIndex,
      tracked: true,
      formattingOrigin: "source",
    });
  }

  return actions;
}

// ── Speaker rules (DIA-1) ─────────────────────────────────────────

/**
 * Evaluate speaker rule DIA-1 for a single paragraph.
 *
 * DIA-1: Apply "diaspk" to the speaker name at the start of a line
 * whose category is listed in the diaspk profile entry's applies_to.
 * The speaker name is identified via an entity hint of type "speaker".
 *
 * @param context - Complete rule evaluation context for the paragraph.
 * @returns Array of declarative actions for the applicator.
 */
export function evaluateSpeakerRules(context: RuleContext): CharacterStyleAction[] {
  const { paragraphCategory, entityHints, characterStyles } = context;

  const diaspkConfig = characterStyles.find((entry) => entry.style === "diaspk");
  if (!diaspkConfig || !canApplyStyle(diaspkConfig, paragraphCategory)) {
    return [];
  }

  const speakerHints = entityHints.filter((hint) => hint.type === "speaker");
  if (speakerHints.length === 0) {
    return [];
  }

  const actions: CharacterStyleAction[] = [];
  for (const hint of speakerHints) {
    // DIA-1: Apply "diaspk" to the speaker name via span-range targeting
    actions.push({
      type: "apply_style",
      style: diaspkConfig.style,
      target: "entity",
      targetText: hint.text,
      targetStartIndex: hint.start_index,
      ruleId: "DIA-1",
      entityType: "speaker",
      tracked: true,
      formattingOrigin: "inference",
    });
  }

  return actions;
}

/** Helper function to analyze the data
function inspectCharacters(text: string) {
  [...text].forEach((char, index) => {
    const code = char.codePointAt(0)!;

    console.log({
      index,
      char: JSON.stringify(char),
      unicode: `U+${code.toString(16).toUpperCase().padStart(4, "0")}`,
      isControl: /\p{Cc}/u.test(char),
      isWhitespace: /\s/u.test(char),
    });
  });
}
**/


// ── Text Message rules (TM-1, TM-2) ──────────────────────────────

/**
 * Evaluate text message rules TM-1 and TM-2 for a single paragraph.
 *
 * TM-1: Apply "tmspkc" to the speaker name identified via a "speaker"
 * entity hint. 
 * TM-2: Apply "tmc" to the text message content span identified via a
 * "text_message" entity hint. Text message character styling should only be
 * used when text messages are inline in paragraphs, not when they are their
 * own paragraphs with text message paragraph styles. 
 * 
 * @param context - Complete rule evaluation context for the paragraph.
 * @returns Array of declarative actions for the applicator.
 */
export function evaluateTextMessageRules(context: RuleContext): CharacterStyleAction[] {
  const { paragraphCategory, entityHints, characterStyles, formatting } = context;

  const tmspkcConfig = characterStyles.find((entry) => entry.style === "tmspkc");
  const tmcConfig = characterStyles.find((entry) => entry.style === "tmc");

  if (!tmspkcConfig || !tmcConfig ) { 
    return [];
  }

  const actions: CharacterStyleAction[] = [];

  // ── TM-1: Text-message speaker ────────────────────────────────────
  //
  // Apply "tmspkc" to a speaker entity at the beginning of the paragraph.
  // TM-1 remains dependent on the tmspkc profile configuration.
  if (tmspkcConfig && canApplyStyle(tmspkcConfig, paragraphCategory)) {
    const speakerHints = entityHints.filter((hint) => hint.type === "speaker");

    for (let spanIndex = 0; spanIndex < formatting.spans.length; spanIndex++) {
      const span = formatting.spans[spanIndex];

      if (!span.text.trim()) {
        continue;
      }

      if (isSpanCoveredByEntityHint(span, speakerHints)) {
        for (const hint of speakerHints) {
          if (hint.start_index === 0) {
            actions.push({
              type: "apply_style",
              style: tmspkcConfig.style,
              target: "entity",
              targetText: hint.text,
              targetStartIndex: hint.start_index,
              ruleId: "TM-1",
              entityType: "speaker",
              tracked: true,
              formattingOrigin: "inference",
            });
          }
        }
      }
    }
  }

  // ── TM-2: Inline text-message content ─────────────────────────────
  //
  // A "tmc" entity can occur anywhere within the paragraph, just like
  // fraction/url entities. Target the entity directly by its exact
  // start index rather than depending on formatting spans.
  if (tmcConfig && canApplyStyle(tmcConfig, paragraphCategory)) {
    const tmcHints = entityHints.filter((hint) => hint.type === "text_message");

    for (const hint of tmcHints) {
      actions.push({
        type: "apply_style",
        style: tmcConfig.style,
        target: "entity",
        targetText: hint.text,
        targetStartIndex: hint.start_index,
        ruleId: "TM-2",
        entityType: "tmc",
        tracked: true,
        formattingOrigin: "inference",
      });
    }
  }

  return actions;
}


// ── URL rules (URL-1) ─────────────────────────────────────────────

/**
 * Evaluate URL rule URL-1 for a single paragraph.
 *
 * URL-1: Apply "url" character style to text identified as a URL via
 * an entity hint of type "url". Gated by the url profile entry's
 * applies_to list.
 *
 * @param context - Complete rule evaluation context for the paragraph.
 * @returns Array of declarative actions for the applicator.
 */
export function evaluateUrlRules(context: RuleContext): CharacterStyleAction[] {
  const { paragraphCategory, entityHints, characterStyles } = context;

  const urlConfig = characterStyles.find((entry) => entry.style === "url");
  if (!urlConfig || !canApplyStyle(urlConfig, paragraphCategory)) {
    return [];
  }

  const urlHints = entityHints.filter((hint) => hint.type === "url");
  if (urlHints.length === 0) {
    return [];
  }

  const actions: CharacterStyleAction[] = [];
  for (const hint of urlHints) {
    // URL-1: Apply "url" to the URL text via span-range targeting
    actions.push({
      type: "apply_style",
      style: urlConfig.style,
      target: "entity",
      targetText: hint.text,
      targetStartIndex: hint.start_index,
      ruleId: "URL-1",
      entityType: "url",
      tracked: true,
      formattingOrigin: "inference",
    });
  }

  return actions;
}

// ── Fraction rules (FRAC-1) ───────────────────────────────────────

/**
 * Evaluate fraction rule FRAC-1 for a single paragraph.
 *
 * FRAC-1: Apply "frac" character style to text identified as a fraction
 * via an entity hint of type "fraction". Gated by the frac profile
 * entry's applies_to list.
 *
 * @param context - Complete rule evaluation context for the paragraph.
 * @returns Array of declarative actions for the applicator.
 */
export function evaluateFractionRules(context: RuleContext): CharacterStyleAction[] {
  const { paragraphCategory, paragraphText, entityHints, characterStyles } = context;

  const fracConfig = characterStyles.find((entry) => entry.style === "frac");
  if (!fracConfig || !canApplyStyle(fracConfig, paragraphCategory)) {
    return [];
  }

  const fractionHints = entityHints.filter((hint) => hint.type === "fraction");
  if (fractionHints.length === 0) {
    return [];
  }

  const actions: CharacterStyleAction[] = [];
  const claimedStarts = new Set<number>();
  for (const hint of fractionHints) {
    // The backend places repeated fraction text at its first occurrence, so a
    // repeat that collides with a claimed index is moved to the next unclaimed one.
    let startIndex = hint.start_index;
    if (claimedStarts.has(startIndex)) {
      startIndex = paragraphText.indexOf(hint.text);
      while (startIndex !== -1 && claimedStarts.has(startIndex)) {
        startIndex = paragraphText.indexOf(hint.text, startIndex + 1);
      }
      if (startIndex === -1) {
        continue;
      }
    }
    claimedStarts.add(startIndex);

    // FRAC-1: Apply "frac" to the fraction text via span-range targeting
    actions.push({
      type: "apply_style",
      style: fracConfig.style,
      target: "entity",
      targetText: hint.text,
      targetStartIndex: startIndex,
      ruleId: "FRAC-1",
      entityType: "fraction",
      tracked: true,
      formattingOrigin: "inference",
    });
  }

  return actions;
}

// ── Orchestrator ──────────────────────────────────────────────────

/**
 * DISABLED: SmallCaps character style rules (SC-1 through SC-4)
 *
 * This function is temporarily disabled due to unreliable Word.js behavior.
 * The font.smallCaps property returns null for mixed formatting, causing
 * inconsistent detection where some words in a SmallCaps run are skipped.
 *
 * Re-enable when:
 * 1. Word.js API provides consistent smallCaps property values, OR
 * 2. An alternative detection strategy is implemented (e.g., text-level only)
 *
 * Original rules (when active):
 * - SC-1: Apply "sc" to uppercase-only spans (no bold, no italic)
 * - SC-2: Apply "sc-i" to uppercase-only spans that are italic (not bold)
 * - SC-3: Apply "sc-b" to uppercase-only spans that are bold (not italic)
 * - SC-4: Apply "sc-bi" to uppercase-only spans that are bold and italic
 *
 * Original detection triggers:
 * - Text-level uppercase (textIsUppercase): author typed in uppercase
 * - Word's SmallCaps formatting (smallCaps): author applied font.SmallCaps
 *
 * @param context - Complete rule evaluation context for the paragraph.
 * @returns Empty array (rules disabled).
 */
export function evaluateSmallCapsRules(context: RuleContext): CharacterStyleAction[] {
  // Function disabled - see documentation above
  return [];
  
  /* DISABLED CODE - Uncomment when re-enabling SmallCaps rules
  
  const { paragraphCategory, formatting, characterStyles } = context;

  
  const { paragraphCategory, formatting, characterStyles } = context;

  // Skip entire-uppercase paragraphs (text-level only; SmallCaps formatting is intentional)
  if (formatting.allUppercase) {
    return [];
  }

  const scConfig = characterStyles.find((entry) => entry.style === "sc");
  const scIConfig = characterStyles.find((entry) => entry.style === "sc-i");
  const scBConfig = characterStyles.find((entry) => entry.style === "sc-b");
  const scBiConfig = characterStyles.find((entry) => entry.style === "sc-bi");

  // If no SC styles are configured at all, bail out
  if (!scConfig && !scIConfig && !scBConfig && !scBiConfig) {
    return [];
  }

  const actions: CharacterStyleAction[] = [];

  for (let spanIndex = 0; spanIndex < formatting.spans.length; spanIndex++) {
    const span = formatting.spans[spanIndex];
    
    // Trigger on EITHER text-level uppercase OR Word SmallCaps formatting
    if ((!span.textIsUppercase && !span.smallCaps) || !span.text.trim()) {
      continue;
    }

    let config;
    if (span.bold && span.italic) {
      config = scBiConfig; // SC-4
    } else if (span.bold) {
      config = scBConfig; // SC-3
    } else if (span.italic) {
      config = scIConfig; // SC-2
    } else {
      config = scConfig; // SC-1
    }

    if (config && canApplyStyle(config, paragraphCategory)) {
      actions.push({
        type: "apply_style",
        style: config.style,
        target: "span",
        spanIndex,
        tracked: true,
        formattingOrigin: "source",
      });
    }
  }

  return actions;
  
  END OF DISABLED CODE */
}

// ── Orchestrator ──────────────────────────────────────────────────

/**
 * Evaluate all character style rules for a single paragraph.
 *
 * Runs italic rules first (I-1 through I-7), then bold rules (B-1
 * through B-3), then bold-italic (BI-1 through BI-4), then
 * underline (U-1 through U-5), then strikethrough (S-1), then
 * speaker (DIA-1), then text message (TM-1, TM-2), then URL (URL-1),
 * then fraction (FRAC-1).
 *
 * NOTE: Small caps rules (SC-1 through SC-4) are temporarily disabled due to
 * inconsistent Word.js behavior with font.smallCaps property returning null
 * for mixed formatting. Re-enable when Word.js API is more reliable or when
 * an alternative detection strategy is implemented.
 *
 * @param context - Complete rule evaluation context for the paragraph.
 * @returns Combined array of actions from all rule groups.
 */
function tagActions(actions: CharacterStyleAction[], ruleGroup: string): CharacterStyleAction[] {
  return actions.map((a) => ({ ...a, ruleId: a.ruleId ?? ruleGroup }));
}

export function evaluateAllRules(context: RuleContext): CharacterStyleAction[] {
  const italicActions = tagActions(evaluateItalicRules(context), "I");
  const boldActions = tagActions(evaluateBoldRules(context), "B");
  const boldItalicActions = tagActions(evaluateBoldItalicRules(context), "BI");
  const underlineActions = tagActions(evaluateUnderlineRules(context), "U");
  const strkActions = tagActions(evaluateStrikethroughRules(context), "S");
  const speakerActions = tagActions(evaluateSpeakerRules(context), "DIA");
  const textMessageActions = tagActions(evaluateTextMessageRules(context), "TM");
  const urlActions = tagActions(evaluateUrlRules(context), "URL");
  const fractionActions = tagActions(evaluateFractionRules(context), "FRAC");

  // DISABLED: Small caps detection unreliable due to Word.js null values
  // const smallCapsActions = tagActions(evaluateSmallCapsRules(context), "SC");

  const combined = [...italicActions, ...boldActions, ...boldItalicActions, ...underlineActions, ...strkActions, ...speakerActions, ...textMessageActions, ...urlActions, ...fractionActions];

  // Multiple rules (italic, bold-italic, underline) each emit entity actions for the same
  // publication/foreign-word hints as a search-based fallback. Deduplicate by (style, targetText)
  // so each unique entity search fires only once against Word.
  const seenEntityKeys = new Set<string>();
  return combined.filter((action) => {
    if (action.type !== "apply_style" || action.target !== "entity") {
      return true;
    }
    const key = `${action.style}|${action.targetText}|${action.targetStartIndex ?? ""}`;
    if (seenEntityKeys.has(key)) {
      console.log(
        `[CharStyles] Dropping duplicate entity action ${action.ruleId ?? ""} ` +
          `"${action.targetText}" (${action.style}) at ${action.targetStartIndex}`
      );
      return false;
    }
    seenEntityKeys.add(key);
    return true;
  });
}